import { Component, Fragment } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import { v1 as uuid } from 'uuid';
import {
  getIn,
  Field,
  connect as connectFormik,
  FieldProps,
  FormikProps,
  FieldArray,
} from 'formik';
import Icon from 'components/Icon';
import Input from 'components/Input';
import Button from 'components/Button';
import Textarea from 'components/Textarea';
import { getProperty } from './Messages';
import { Delete, FieldArraysWrapper } from '../styles';
import { AddChoices, ScoreFieldWrapper, ChoiceWrapper } from './styles';
import Select from '../../../../../Select';
import { getTags } from '../GeneralQuestion/GeneralQuestionEditor';
import {
  IChoiceView,
  IFormValues,
  IQuestionTags,
} from 'store/survey/surveyTypes';
import { setClipboard } from '../../../../../../containers/Tasks/TasksManagement/TaskUtils';
import notify from '../../../../../Notify';
import { readClipboard } from '../../../../../../utils/happyFunctions';
import i18n from '../../../../../../i18n';

interface IFormikConnect {
  formik: FormikProps<IFormValues>;
}

interface IOwnProps {
  hasScore: boolean;
  messageName: 'choiceList' | 'parts';
  preventEdit?: boolean;
  allowMigrateToTag?: boolean;
  tags?: IQuestionTags;
  isExam?: boolean;
  children?: React.ReactNode;
}
interface IState {
  enableReorder: boolean;
  enableMigrateToTag: boolean;
}

type IComponentProps = IOwnProps & IFormikConnect;

class MultiChoiceEditor extends Component<IComponentProps, IState> {
  state = {
    enableReorder: false,
    enableMigrateToTag: false,
  };
  onPasteIntoTextArea = (e: any) => {
    const pastedText = e.clipboardData.getData('text/html');
    const isTableExists = pastedText.search(/<table/gm);
    if (isTableExists !== -1) {
      // if pasted text is a table
      const tdTags = pastedText
        .match(/<td[^>]+?>([^$]+?)<\/td>/gim)
        .map((td: any) => {
          const span = document.createElement('span');
          span.innerHTML = td;
          const str = span.textContent || span.innerText;
          return str.match(/[^\s](.*)[^\s]/gim)!.join(' ');
        });
      const foundItems = tdTags
        .filter((text: string) => text !== null)
        .reduce((items: Array<{ text: string; score: null }>, text: string) => {
          items.push({ text, score: null });
          return items;
        }, []);
      if (foundItems.length > 1) {
        const { messageName } = this.props;
        const question = this.props.formik.values;
        const choices = getIn(question, ['data', messageName]);

        // if there were more than one cell in clipboard otherwise, simply do paste text
        e.preventDefault();
        const prevChoices = cloneDeep(choices);
        const prevChoicesWithoutEmptyText = prevChoices.filter(
          (choice: any) => choice.text !== ''
        );
        const newChoices = [...prevChoicesWithoutEmptyText, ...foundItems];
        this.props.formik.setFieldValue(`data[${messageName}]`, newChoices);
      }
    }
  };

  render() {
    const {
      hasScore,
      children,
      messageName,
      preventEdit,
      allowMigrateToTag,
      tags,
      isExam,
    } = this.props;
    const { enableReorder, enableMigrateToTag } = this.state;
    const question = this.props.formik.values;
    const choices = getIn(question, ['data', messageName]);

    return (
      <FieldArray name={`data[${messageName}]`}>
        {({ push, remove, move }) => (
          <FieldArraysWrapper>
            <AddChoices>
              {children}
              <div style={{ display: 'flex', gap: '5px' }}>
                {!isExam && (
                  <>
                    {enableReorder && (
                      <span>
                        {i18n.t('moveHint', { ns: 'survey' }) as string}
                      </span>
                    )}
                    {enableMigrateToTag && (
                      <span>
                        {i18n.t('migrateToTagHint', { ns: 'survey' }) as string}
                      </span>
                    )}

                    <Button
                      onClick={() =>
                        this.setState({ enableReorder: !enableReorder })
                      }
                    >
                      <Icon style={{ margin: '2px' }} type="move" />
                    </Button>

                    <Button
                      onClick={() => {
                        setClipboard(JSON.stringify(choices));
                        notify(i18n.t('copied', { ns: 'survey' }) as string);
                      }}
                    >
                      <Icon
                        type="copy"
                        title={i18n.t('copy', { ns: 'survey' }) as string}
                      />
                    </Button>

                    <Button
                      onClick={() => {
                        readClipboard((content) => {
                          try {
                            const value = JSON.parse(content);
                            if (Array.isArray(value)) {
                              this.props.formik.setFieldValue(
                                `data[${messageName}]`,
                                value
                              );
                            }
                          } catch (error) {
                            console.error('Invalid clipboard content', error);
                          }
                        });
                      }}
                    >
                      <Icon
                        type="clipboard"
                        title={i18n.t('paste', { ns: 'survey' }) as string}
                      />
                    </Button>

                    {allowMigrateToTag && (
                      <Button
                        onClick={() => {
                          this.setState({
                            enableMigrateToTag: !enableMigrateToTag,
                          });
                          if (enableMigrateToTag) {
                            this.props.formik.setFieldValue(
                              'data.migrateToTags',
                              {}
                            );
                          }
                        }}
                        color={(enableMigrateToTag ? 'blue' : undefined) as any}
                      >
                        <Icon
                          title={
                            i18n.t('migrateToTag', { ns: 'survey' }) as string
                          }
                          type="tag"
                        />
                      </Button>
                    )}
                  </>
                )}

                <Button
                  onClick={() =>
                    push({
                      score: isExam && !choices.length ? 1 : isExam ? 0 : null,
                      text: '',
                    })
                  }
                >
                  <Icon type="plus-circle" className="right" />
                  {getProperty[messageName].addChoice}
                </Button>
              </div>
            </AddChoices>
            {choices &&
              choices.map(
                (
                  choice: IChoiceView,
                  index: number,
                  allChoice: IChoiceView[]
                ) => {
                  const textInputId = uuid();
                  const hasScoreInputId = uuid();
                  const scoreInputId = uuid();
                  const weightInputId = uuid();
                  // const moveUp = () => {
                  //   move(index, index - 1);
                  // };
                  // const moveDown = () => {
                  //   move(index, index + 1);
                  // };
                  return (
                    <ChoiceWrapper key={`${messageName}.${index}`}>
                      <span style={{ display: 'block' }}>
                        {enableReorder && (
                          <div style={{ display: 'flex' }}>
                            <span>{index + 1}.</span>
                            <Input
                              id={question.data.id + '-' + index}
                              type="number"
                              style={{ width: '50px' }}
                              defaultValue={index + 1}
                              onKeyDown={(event) => {
                                if (
                                  Number(event.currentTarget.value) &&
                                  event.keyCode === 13
                                ) {
                                  event.preventDefault();
                                  move(
                                    index,
                                    Number(event.currentTarget.value) - 1
                                  );
                                  event.currentTarget.value = (
                                    index + 1
                                  ).toString();
                                }
                              }}
                            />
                          </div>
                        )}
                        {/*{enableReorder && index > 0 ? (
                        <HoverIcon
                          color="blue"
                          type="arrow-up"
                          onClick={moveUp}
                        />
                      ) : (
                        <div style={{ width: '0.5rem', height: '0.5rem' }} />
                      )}
                      {enableReorder && choices.length > index + 1 ? (
                        <HoverIcon
                          color="blue"
                          type="arrow-down"
                          onClick={moveDown}
                        />
                      ) : (
                        <div style={{ width: '0.5rem', height: '0.5rem' }} />
                      )}*/}
                      </span>
                      <Delete
                        type="x"
                        color="red"
                        onClick={() => remove(index)}
                      />

                      <Field name={`data[${messageName}].${index}.text`}>
                        {({ field }: FieldProps) => (
                          <Fragment>
                            {hasScore ? (
                              <Input
                                {...field}
                                id={textInputId}
                                disabled={preventEdit && !!choice.value}
                                type="text"
                                required={true}
                                onPaste={this.onPasteIntoTextArea}
                                wrapperClassName="choice-field"
                                wrapperStyle={{
                                  marginInlineEnd: '2rem',
                                  marginInlineStart: '0.5rem',
                                }}
                                placeholder={getProperty[
                                  messageName
                                ].choiceTitle(index + 1)}
                              />
                            ) : (
                              <Textarea
                                id={textInputId}
                                disabled={preventEdit && !!choice.value}
                                {...field}
                                style={{ resize: 'none' }}
                                required={true}
                                onPaste={this.onPasteIntoTextArea}
                                wrapperClassName="choice-field"
                                placeholder={getProperty[
                                  messageName
                                ].choiceTitle(index + 1)}
                              />
                            )}
                          </Fragment>
                        )}
                      </Field>
                      {messageName === 'parts' && (
                        <Field name={`data.parts.${index}.weight`}>
                          {({ field }: FieldProps) => (
                            <Fragment>
                              <Input
                                {...field}
                                autoComplete="off"
                                defaultValue="1"
                                id={weightInputId}
                                label={getProperty.parts.weightTitle(index + 1)}
                                placeholder={getProperty.parts.weightTitle(
                                  index + 1
                                )}
                                required={true}
                                step="any"
                                style={{ textAlign: 'center' }}
                                type="number"
                                min=""
                                wrapperClassName="question-input-small"
                              />
                            </Fragment>
                          )}
                        </Field>
                      )}

                      {enableMigrateToTag && tags ? (
                        <Field>
                          {({ form }: FieldProps) => (
                            <Select
                              multiChoice={false}
                              options={getTags(this.props.tags || {}, false)}
                              isRequired={true}
                              values={
                                form.values.data?.migrateToTags?.[choice.value]
                                  ? [
                                      {
                                        value: Number(
                                          form.values.data.migrateToTags[
                                            choice.value
                                          ]
                                        ),
                                        label:
                                          tags[
                                            form.values.data.migrateToTags[
                                              choice.value
                                            ]
                                          ].text,
                                      },
                                    ]
                                  : []
                              }
                              onChangeValues={(values) => {
                                form.setFieldValue(
                                  `data.migrateToTags.${choice.value}`,
                                  values[0].value
                                );
                              }}
                              inputStyle={{
                                minHeight: 'initial',
                                marginTop: '3px',
                              }}
                              placeholder={
                                i18n.t('searchTag', { ns: 'survey' }) as string
                              }
                            />
                          )}
                        </Field>
                      ) : (
                        hasScore && (
                          <ScoreFieldWrapper>
                            {!isExam && (
                              <Field>
                                {({ form, field }: FieldProps) => (
                                  <Input
                                    {...field}
                                    id={hasScoreInputId}
                                    type="checkbox"
                                    checked={choice.score !== null}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        form.setFieldValue(
                                          `data[${messageName}].${index}.score`,
                                          1
                                        );
                                      } else {
                                        form.setFieldValue(
                                          `data[${messageName}].${index}.score`,
                                          null
                                        );
                                      }
                                    }}
                                    label={
                                      i18n.t('hasScore', {
                                        ns: 'survey',
                                      }) as string
                                    }
                                  />
                                )}
                              </Field>
                            )}
                            {choice.score !== null && (
                              <Field
                                name={`data[${messageName}].${index}.score`}
                              >
                                {({ field, form }: FieldProps) =>
                                  isExam ? (
                                    <div style={{ display: 'flex' }}>
                                      <Input
                                        id={scoreInputId}
                                        type="checkbox"
                                        checked={!!field.value}
                                        onChange={(e) => {
                                          if (!e.target.checked) return;
                                          allChoice.forEach((_, cindex) => {
                                            form.setFieldValue(
                                              `data[${messageName}].${cindex}.score`,
                                              0
                                            );
                                          });
                                          form.setFieldValue(
                                            `data[${messageName}].${index}.score`,
                                            1
                                          );
                                        }}
                                      />
                                      {field.value
                                        ? (i18n.t('correctAnswer', {
                                            ns: 'survey',
                                          }) as string)
                                        : ''}
                                    </div>
                                  ) : (
                                    <Input
                                      {...field}
                                      id={scoreInputId}
                                      step="any"
                                      type="number"
                                      required={true}
                                      style={{ textAlign: 'center' }}
                                      label={
                                        i18n.t('score', {
                                          ns: 'survey',
                                        }) as string
                                      }
                                      placeholder={
                                        i18n.t('score', {
                                          ns: 'survey',
                                        }) as string
                                      }
                                      wrapperClassName="question-input-small"
                                    />
                                  )
                                }
                              </Field>
                            )}
                          </ScoreFieldWrapper>
                        )
                      )}
                    </ChoiceWrapper>
                  );
                }
              )}
            {enableMigrateToTag && tags && (
              <Field>
                {({ form }: FieldProps) => (
                  <Select
                    multiChoice={false}
                    options={getTags(this.props.tags || {}, false)}
                    isRequired={false}
                    values={
                      form.values.data?.migrateToTags?.['']
                        ? [
                            {
                              value: Number(form.values.data.migrateToTags['']),
                              label:
                                tags[form.values.data.migrateToTags['']].text,
                            },
                          ]
                        : []
                    }
                    onChangeValues={(values) => {
                      form.setFieldValue(
                        'data.migrateToTags.',
                        values[0].value
                      );
                    }}
                    inputStyle={{
                      minHeight: 'initial',
                      marginTop: '3px',
                    }}
                    label={
                      i18n.t('missingOrDeletedOption', {
                        ns: 'survey',
                      }) as string
                    }
                    placeholder={
                      i18n.t('searchTag', { ns: 'survey' }) as string
                    }
                  />
                )}
              </Field>
            )}
          </FieldArraysWrapper>
        )}
      </FieldArray>
    );
  }
}

export default connectFormik<IOwnProps, IFormValues>(MultiChoiceEditor);
